import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import { Sparkles, Loader2, Target, Users, ThumbsUp, ThumbsDown, CheckCircle2, XCircle, CircleDashed, BrainCircuit, Trash2, FlaskConical, Edit3 } from "lucide-react";
import RejectReasonModal from "@/components/common/RejectReasonModal";

export default function ToneOfVoice() {
    const { t } = useTranslation();
    const [tones, setTones] = useState({ sales: null, recruiter: null });
    const [essence, setEssence] = useState({ values: [], culture: "", voice_description: "" });
    const [preview, setPreview] = useState({ sales: "", recruiter: "" });
    const [generating, setGenerating] = useState(null);
    const [pendingReject, setPendingReject] = useState(null); // { type }

    const load = async () => {
        const [a, b] = await Promise.all([api.get("/tone"), api.get("/auth/me")]);
        setTones(a.data);
        setEssence(b.data.workspace?.brand_essence || { values: [], culture: "", voice_description: "" });
    };
    useEffect(() => { load(); }, []);

    const patchTone = async (type, patch) => {
        try { await api.patch(`/tone/${type}`, patch); toast.success(t("tone.updated")); load(); }
        catch (e) { toast.error(t("tone.failed")); }
    };
    const patchEssence = async () => {
        try { await api.patch("/tone/brand/essence", essence); toast.success(t("tone.essence_saved")); }
        catch (e) { toast.error(t("tone.failed")); }
    };
    const generate = async (type) => {
        setGenerating(type);
        try { const r = await api.post("/tone/preview", { master_type: type, scenario: "first_touch" }); setPreview((p) => ({...p, [type]: r.data.preview})); }
        catch (e) { toast.error(e?.response?.data?.detail || t("tone.preview_failed")); }
        finally { setGenerating(null); }
    };

    const approveTone = async (type, comment) => {
        try {
            await api.post(`/tone/${type}/feedback`, { approved: true, comment: comment || null, preview_snippet: preview[type] || null });
            toast.success(t("tone.approve_success"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("tone.failed")); }
    };
    const confirmReject = async (reason, category, remember) => {
        const type = pendingReject?.type;
        if (!type) return;
        try {
            await api.post(`/tone/${type}/feedback`, {
                approved: false, comment: reason, category, remember,
                preview_snippet: preview[type] || null,
            });
            toast.success(t("tone.reject_success"));
            setPendingReject(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("tone.failed")); }
    };

    if (!tones.sales || !tones.recruiter) return <PageHeader title={t("pages.tone_title")}><div className="text-[var(--jr-text-2)]">{t("common.loading")}</div></PageHeader>;

    return (
        <PageHeader title={t("pages.tone_title")} subtitle={t("pages.tone_subtitle")}>
            {/* Brand essence */}
            <div className="rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6 mb-6">
                <div className="flex items-center gap-2 mb-4"><Sparkles className="text-[var(--jr-brand)]" size={18}/><h2 className="font-display text-xl tracking-tight">{t("tone.brand_essence_title")}</h2></div>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("tone.core_values")}</label>
                        <textarea value={(essence.values || []).join("\n")} onChange={(e) => setEssence({...essence, values: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean)})} data-testid="essence-values" rows={5} placeholder="transparant&#10;menselijk" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("tone.culture")}</label>
                        <textarea value={essence.culture || ""} onChange={(e) => setEssence({...essence, culture: e.target.value})} data-testid="essence-culture" rows={5} className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("tone.brand_voice")}</label>
                        <textarea value={essence.voice_description || ""} onChange={(e) => setEssence({...essence, voice_description: e.target.value})} data-testid="essence-voice" rows={5} className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    </div>
                </div>
                <button onClick={patchEssence} data-testid="essence-save" className="mt-4 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">{t("common.save")}</button>
            </div>

            {/* Per master */}
            <div className="grid lg:grid-cols-2 gap-6">
                <ToneEdit type="sales" label="Sales Master" Icon={Target} accent="var(--jr-brand)" tone={tones.sales} preview={preview.sales} generating={generating === "sales"} onPatch={(p) => patchTone("sales", p)} onGenerate={() => generate("sales")} onApprove={(c) => approveTone("sales", c)} onReject={() => setPendingReject({ type: "sales" })}/>
                <ToneEdit type="recruiter" label="Recruiter Master" Icon={Users} accent="var(--jr-amber)" tone={tones.recruiter} preview={preview.recruiter} generating={generating === "recruiter"} onPatch={(p) => patchTone("recruiter", p)} onGenerate={() => generate("recruiter")} onApprove={(c) => approveTone("recruiter", c)} onReject={() => setPendingReject({ type: "recruiter" })}/>
            </div>

            {/* Sprint 6 · #19 — Zelflerende stijlregels */}
            <LearnedRules tone={tones.sales} onChanged={load} />

            <RejectReasonModal
                open={!!pendingReject}
                onClose={() => setPendingReject(null)}
                title={t("tone.reject_modal_title")}
                subtitle={pendingReject?.type === "sales" ? "Sales Master" : "Recruiter Master"}
                originalSnippet={pendingReject ? (preview[pendingReject.type] || "") : ""}
                onConfirm={confirmReject}
            />
        </PageHeader>
    );
}

function ApprovalBadge({ tone, t }) {
    const status = tone.approval_status;
    const when = tone.approved_at ? new Date(tone.approved_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
    if (status === "approved") {
        return (
            <span data-testid="tone-status-approved" title={tone.approval_comment || ""}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-[#22c55e]/12 text-[#22c55e] border border-[#22c55e]/30">
                <CheckCircle2 size={11}/> {t("tone.status_approved")}{tone.approved_by_name ? ` · ${tone.approved_by_name}` : ""}{when ? ` · ${when}` : ""}
            </span>
        );
    }
    if (status === "rejected") {
        return (
            <span data-testid="tone-status-rejected" title={tone.approval_comment || ""}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-[var(--jr-error)]/12 text-[var(--jr-error)] border border-[var(--jr-error)]/30">
                <XCircle size={11}/> {t("tone.status_rejected")}{tone.approved_by_name ? ` · ${tone.approved_by_name}` : ""}
            </span>
        );
    }
    return (
        <span data-testid="tone-status-pending"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-white/[0.04] text-[var(--jr-text-3)] border border-white/10">
            <CircleDashed size={11}/> {t("tone.status_pending")}
        </span>
    );
}

function ToneEdit({ type, label, Icon, accent, tone, preview, generating, onPatch, onGenerate, onApprove, onReject }) {
    const { t } = useTranslation();
    const STYLE = {
        formality: t("tone.style_formality", { returnObjects: true }),
        energy: t("tone.style_energy", { returnObjects: true }),
        warmth: t("tone.style_warmth", { returnObjects: true }),
    };
    const [local, setLocal] = useState({
        formality: tone.formality, energy: tone.energy, warmth: tone.warmth,
        default_greeting: tone.default_greeting, default_signoff: tone.default_signoff,
        forbidden: (tone.forbidden_phrases || []).join("\n"),
        samples: (tone.sample_messages || []).join("\n---\n"),
        extra_instructions: tone.extra_instructions || "",
    });
    const [approving, setApproving] = useState(false);
    const [approveComment, setApproveComment] = useState("");
    const save = () => onPatch({
        formality: local.formality, energy: local.energy, warmth: local.warmth,
        default_greeting: local.default_greeting, default_signoff: local.default_signoff,
        forbidden_phrases: local.forbidden.split("\n").map((s) => s.trim()).filter(Boolean),
        sample_messages: local.samples.split(/---+/).map((s) => s.trim()).filter(Boolean),
        extra_instructions: local.extra_instructions,
    });
    const confirmApprove = async () => {
        await onApprove?.(approveComment.trim());
        setApproving(false);
        setApproveComment("");
    };
    return (
        <div className="rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6" data-testid={`tone-edit-${type}`}>
            <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
                <div className="flex items-center gap-2"><Icon style={{ color: accent }} size={18}/><h3 className="font-display text-xl tracking-tight">{label}</h3></div>
                <ApprovalBadge tone={tone} t={t} />
            </div>
            <div className="grid grid-cols-3 gap-3">
                {["formality", "energy", "warmth"].map((k) => (
                    <div key={k}>
                        <div className="flex items-center justify-between"><span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{k}</span><span className="text-[10px]" style={{ color: accent }}>{STYLE[k][local[k] - 1]}</span></div>
                        <input type="range" min={1} max={4} value={local[k]} onChange={(e) => setLocal({...local, [k]: Number(e.target.value)})} data-testid={`tone-${type}-${k}`} className="mt-2 w-full" style={{ accentColor: accent }}/>
                    </div>
                ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
                <input value={local.default_greeting} onChange={(e) => setLocal({...local, default_greeting: e.target.value})} data-testid={`tone-${type}-greeting`} placeholder="Hi {first_name}" className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                <input value={local.default_signoff} onChange={(e) => setLocal({...local, default_signoff: e.target.value})} data-testid={`tone-${type}-signoff`} placeholder="— Sender" className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
            </div>
            <textarea value={local.forbidden} onChange={(e) => setLocal({...local, forbidden: e.target.value})} data-testid={`tone-${type}-forbidden`} rows={2} placeholder={t("tone.forbidden_placeholder")} className="mt-3 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
            <textarea value={local.samples} onChange={(e) => setLocal({...local, samples: e.target.value})} data-testid={`tone-${type}-samples`} rows={4} placeholder={t("tone.samples_placeholder")} className="mt-3 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
            <div className="mt-4 flex justify-between items-center">
                <button onClick={save} data-testid={`tone-${type}-save`} className="px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">{t("common.save")}</button>
                <button onClick={onGenerate} disabled={generating} data-testid={`tone-${type}-generate`} className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)]">{generating ? <Loader2 className="animate-spin" size={12}/> : <Sparkles size={12}/>} {t("tone.live_preview")}</button>
            </div>
            {preview && (
                <div className="mt-4 rounded-lg border-l-2 p-4 text-sm text-white whitespace-pre-wrap leading-relaxed bg-[var(--jr-surface-2)]" style={{ borderColor: accent }} data-testid={`tone-${type}-preview`}>{preview}</div>
            )}

            {/* Approve / Reject — mirrors the signal-scan flow */}
            <div className="mt-4 pt-4 border-t border-white/8">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("tone.feedback_question")}</span>
                    <div className="flex items-center gap-1.5">
                        <button onClick={onReject} data-testid={`tone-${type}-reject`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] text-xs font-medium hover:bg-[var(--jr-error)]/20">
                            <ThumbsDown size={12}/> {t("tone.reject")}
                        </button>
                        <button onClick={() => setApproving((v) => !v)} data-testid={`tone-${type}-approve`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22c55e] text-black text-xs font-medium hover:bg-[#16a34a]">
                            <ThumbsUp size={12}/> {t("tone.approve")}
                        </button>
                    </div>
                </div>
                {approving && (
                    <div className="mt-3 rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.05] p-3" data-testid={`tone-${type}-approve-panel`}>
                        <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1.5">{t("tone.approve_comment_label")}</label>
                        <textarea value={approveComment} onChange={(e) => setApproveComment(e.target.value)} rows={2}
                            placeholder={t("tone.approve_comment_placeholder")}
                            data-testid={`tone-${type}-approve-comment`}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#22c55e] resize-none"/>
                        <div className="mt-2 flex justify-end gap-2">
                            <button onClick={() => { setApproving(false); setApproveComment(""); }} data-testid={`tone-${type}-approve-cancel`}
                                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">{t("common.cancel")}</button>
                            <button onClick={confirmApprove} data-testid={`tone-${type}-approve-confirm`}
                                className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full bg-[#22c55e] text-black font-medium hover:bg-[#16a34a]">
                                <ThumbsUp size={11}/> {t("tone.approve_confirm")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Sprint 6 · #19 — Zelflerende stijlregels: gedestilleerd uit menselijke edits
 * van AI-drafts (bij verzending) en uit A/B-winnaars. Elke regel gaat mee in
 * alle toekomstige draft-prompts. Beheer: verwijderen + leren aan/uit.
 */
function LearnedRules({ tone, onChanged }) {
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(true);
    const rules = tone?.learned_rules || [];

    useEffect(() => {
        api.get("/tone/learning/settings").then((r) => setEnabled(!!r.data.enabled)).catch(() => {});
    }, []);

    const toggle = async () => {
        const next = !enabled;
        setEnabled(next); // optimistisch — revert bij fout
        try {
            const { data } = await api.patch("/tone/learning/settings", { enabled: next });
            setEnabled(!!data.enabled);
            toast.success(data.enabled ? t("tone.learned_on") : t("tone.learned_off"));
        } catch (e) {
            setEnabled(!next);
            toast.error(e?.response?.data?.detail || t("tone.failed"));
        }
    };
    const removeRule = async (ruleId) => {
        try {
            await api.delete(`/tone/sales/learned/${ruleId}`);
            toast.success(t("tone.learned_removed"));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || t("tone.failed")); }
    };

    return (
        <div className="mt-6 rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6" data-testid="learned-rules-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-2">
                    <BrainCircuit className="text-[#c084fc]" size={18}/>
                    <h2 className="font-display text-xl tracking-tight">{t("tone.learned_title")}</h2>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#c084fc]/30 text-[#c084fc]">{rules.length}/12</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-[var(--jr-text-2)] cursor-pointer" data-testid="learned-toggle-label">
                    <input type="checkbox" checked={enabled} onChange={toggle} data-testid="learned-toggle" className="accent-[#c084fc]"/>
                    {t("tone.learned_toggle")}
                </label>
            </div>
            <p className="text-sm text-[var(--jr-text-2)] mb-4">{t("tone.learned_sub")}</p>
            {rules.length === 0 ? (
                <div className="text-sm text-[var(--jr-text-3)] rounded-lg border border-dashed border-white/10 px-4 py-5 text-center" data-testid="learned-empty">
                    {t("tone.learned_empty")}
                </div>
            ) : (
                <div className="space-y-2">
                    {rules.slice().reverse().map((r) => (
                        <div key={r.id} className="flex items-start gap-2.5 rounded-lg border border-white/8 bg-[var(--jr-surface-2)] px-3 py-2.5" data-testid={`learned-rule-${r.id}`}>
                            {r.source === "ab_winner"
                                ? <FlaskConical size={13} className="text-[#c084fc] mt-0.5 shrink-0" title={t("tone.learned_src_ab")}/>
                                : <Edit3 size={13} className="text-[var(--jr-brand)] mt-0.5 shrink-0" title={t("tone.learned_src_edit")}/>}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-white leading-relaxed">{r.rule}</div>
                                <div className="text-[10px] font-mono text-[var(--jr-text-3)] mt-0.5">
                                    {r.source === "ab_winner" ? t("tone.learned_src_ab") : t("tone.learned_src_edit")}
                                    {r.learned_at ? ` · ${new Date(r.learned_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "short" })}` : ""}
                                </div>
                            </div>
                            <button onClick={() => removeRule(r.id)} data-testid={`learned-delete-${r.id}`}
                                title={t("common.delete")}
                                className="p-1.5 rounded hover:bg-white/8 text-[var(--jr-text-3)] hover:text-[var(--jr-error)] shrink-0">
                                <Trash2 size={13}/>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
